/**
 * supportFormat.ts — the Support Desk's pure helpers.
 *
 * Everything here is a function of its arguments: label and colour tables for
 * statuses and priorities, the eligibility wording, reply-template placeholder
 * substitution, URL autolinking of plain-text message bodies, SLA countdown
 * state and the duration/size formatting that goes with it. Keeping it out of
 * the components is what lets it be covered to 100% — Svelte files are not.
 *
 * Labels are returned as the **English key**, not a translated string: the
 * components pass them through `$t`, which keeps them reactive to a locale
 * change, and keeps this module free of the i18n store.
 */

import type {
	SupportAttachment,
	SupportCustomerStatus,
	SupportEligibilityReason,
	SupportMessage,
	SupportPerson,
	SupportReplyBlockedReason,
	SupportTicketPriority,
	SupportTicketStatus
} from '../AppTypes';

/** Badge colour union accepted by flowbite-svelte `Badge`. */
export type SupportBadgeColor = 'red' | 'yellow' | 'green' | 'indigo' | 'purple' | 'pink' | 'blue' | 'gray' | 'primary';

/** Who is looking at a ticket — the same status reads differently from each side of the desk. */
export type SupportAudience = 'customer' | 'agent';

/** Every status, in lifecycle order. */
export const SUPPORT_STATUSES: SupportTicketStatus[] = ['new', 'awaiting_agent', 'awaiting_customer', 'resolved', 'closed'];

/** The statuses that count as open: nobody has resolved the question yet. */
export const SUPPORT_OPEN_STATUSES: SupportTicketStatus[] = ['new', 'awaiting_agent', 'awaiting_customer'];

/**
 * How many people a ticket may hold besides its owner. The server enforces it;
 * the client stops offering the field there rather than letting an add fail.
 */
export const SUPPORT_MAX_PARTICIPANTS = 20;

/** Every priority, least urgent first. */
export const SUPPORT_PRIORITIES: SupportTicketPriority[] = ['low', 'normal', 'high', 'urgent'];

/**
 * Badge colour per status.
 *
 * The two states that want someone's attention carry colour — blue for a fresh
 * ticket, amber for one waiting on the team — while waiting on the customer is
 * purple (someone else's move), and the finished states fade to green and grey.
 */
export const SUPPORT_STATUS_COLORS: Record<SupportTicketStatus, SupportBadgeColor> = {
	new: 'blue',
	awaiting_agent: 'yellow',
	awaiting_customer: 'purple',
	resolved: 'green',
	closed: 'gray'
};

/** Badge colour per priority — only the two above normal are allowed to be loud. */
export const SUPPORT_PRIORITY_COLORS: Record<SupportTicketPriority, SupportBadgeColor> = {
	low: 'gray',
	normal: 'blue',
	high: 'yellow',
	urgent: 'red'
};

/**
 * The label of a status, for the side of the desk reading it.
 *
 * `awaiting_agent` is "waiting on us" to a customer and "needs a reply" to an
 * agent; naming it from the reader's side is what makes it say whose move it is.
 */
export function supportStatusLabel(status: SupportTicketStatus, audience: SupportAudience = 'agent'): string {
	const customer: Record<SupportTicketStatus, string> = {
		new: 'Open',
		awaiting_agent: 'Waiting for support',
		awaiting_customer: 'Waiting for you',
		resolved: 'Resolved',
		closed: 'Closed'
	};
	const agent: Record<SupportTicketStatus, string> = {
		new: 'New',
		awaiting_agent: 'Awaiting agent',
		awaiting_customer: 'Awaiting customer',
		resolved: 'Resolved',
		closed: 'Closed'
	};
	return (audience === 'customer' ? customer : agent)[status] ?? status;
}

/** The label of a priority. */
export function supportPriorityLabel(priority: SupportTicketPriority): string {
	const labels: Record<SupportTicketPriority, string> = {
		low: 'Low',
		normal: 'Normal',
		high: 'High',
		urgent: 'Urgent'
	};
	return labels[priority] ?? priority;
}

/**
 * What a status means, for the second line under it in the desk's dropdowns.
 *
 * Told from the desk's side — every dropdown that offers a status is an agent's — and
 * naming the SLA consequence, since that is what changing a status actually does.
 */
export function supportStatusDescription(status: SupportTicketStatus): string {
	const descriptions: Record<SupportTicketStatus, string> = {
		new: 'Filed and not yet answered; the first-response clock is running.',
		awaiting_agent: 'The customer wrote last, so the desk owes a reply.',
		awaiting_customer: 'The desk replied and waits on the customer; the resolution clock is paused.',
		resolved: 'Answered; the customer can still reply, which reopens it, until it closes.',
		closed: 'Finished; replies are refused until the ticket is reopened.'
	};
	return descriptions[status] ?? '';
}

/** What a priority means, for the second line under it in a dropdown. */
export function supportPriorityDescription(priority: SupportTicketPriority): string {
	const descriptions: Record<SupportTicketPriority, string> = {
		low: 'Not time-sensitive; the most relaxed response and resolution targets.',
		normal: 'The usual question; the standard response and resolution targets.',
		high: 'Work is impaired; tighter response and resolution targets.',
		urgent: 'Work is blocked; the tightest response and resolution targets.'
	};
	return descriptions[priority] ?? '';
}

/**
 * Why support is unavailable, in the customer's terms.
 *
 * Covers the eligibility reasons and the reply-only ones (`closed`,
 * `not-requester`, `unavailable` when the licensing backend could not be asked,
 * and `owner-unsupported` for a participant whose way in — the owner's support —
 * has lapsed). `eligible` has nothing to explain and returns an empty string.
 */
export function supportReasonText(reason: SupportEligibilityReason | SupportReplyBlockedReason): string {
	const reasons: Record<SupportReplyBlockedReason, string> = {
		eligible: '',
		'not-configured': 'Support is not available for this product yet.',
		'no-license': 'You do not hold a license for this product.',
		expired: 'Your license for this product has expired.',
		'no-entitlement': 'Your license for this product does not include support.',
		closed: 'This ticket is closed.',
		'not-requester': 'Only the person who opened this ticket can reply to it.',
		unavailable: 'Your support entitlement could not be confirmed right now. Please try again shortly.',
		'owner-unsupported': "The ticket owner's support is no longer active, so replies are closed."
	};
	return reasons[reason] ?? '';
}

/**
 * Why a ticket's customer no longer has support, told to the desk — the same
 * verdicts as {@link supportReasonText}, but about the customer rather than to
 * them. Empty when there is nothing to explain.
 */
export function customerSupportText(reason: SupportCustomerStatus['reason']): string {
	const reasons: Record<NonNullable<SupportCustomerStatus['reason']>, string> = {
		eligible: '',
		'not-configured': "Support is not configured on this product's licensing server.",
		'no-license': 'They no longer hold a license for this product.',
		expired: 'Their license for this product has expired.',
		'no-entitlement': 'Their license for this product does not include support.',
		'account-inactive': 'Their account has been deactivated.',
		unavailable: 'The licensing server could not be reached to check.'
	};
	return reason ? reasons[reason] : '';
}

/** The placeholders a reply template may contain, with what each becomes. */
export const REPLY_TEMPLATE_PLACEHOLDERS: { key: string; description: string }[] = [
	{ key: 'FIRST_NAME', description: "The requester's first name" },
	{ key: 'FULL_NAME', description: "The requester's full name" },
	{ key: 'TICKET_KEY', description: 'The ticket reference, e.g. LOD-1042' },
	{ key: 'PRODUCT_NAME', description: 'The product the ticket is filed against' },
	{ key: 'AGENT_NAME', description: 'Your name' }
];

/** The values a reply template's placeholders are filled from. */
export interface ReplyTemplateContext {
	/** The requester's full display name. */
	requesterName: string;
	/** The ticket reference. */
	ticketKey: string;
	/** The product's name. */
	productName: string;
	/** The inserting agent's name. */
	agentName: string;
}

/**
 * Fills in a reply template's placeholders.
 *
 * Done client-side, as the body is inserted, so the agent sees — and can still
 * edit — exactly what will be sent. The first name is the requester's name up to
 * its first space, which is the best a single display name allows. An unknown
 * placeholder is left as written rather than blanked: a visible `{{TYPO}}` gets
 * noticed before sending, an empty gap does not.
 */
export function substituteReplyTemplate(body: string, context: ReplyTemplateContext): string {
	const fullName = context.requesterName.trim();
	const values: Record<string, string> = {
		FIRST_NAME: fullName.split(/\s+/)[0],
		FULL_NAME: fullName,
		TICKET_KEY: context.ticketKey,
		PRODUCT_NAME: context.productName,
		AGENT_NAME: context.agentName
	};
	return body.replace(/\{\{\s*([A-Z_]+)\s*\}\}/g, (token: string, key: string) =>
		key in values ? values[key] : token
	);
}

/** One run of a message body: plain text, or a URL to render as a link. */
export interface TextSegment {
	/** Which it is. */
	kind: 'text' | 'link';
	/** The characters, exactly as written. */
	text: string;
}

/** An http(s) URL, up to the first character that cannot appear unescaped in one. */
const URL_PATTERN = /\bhttps?:\/\/[^\s<>"'`]+/gi;

/**
 * Trims punctuation that ends a sentence rather than the URL it follows.
 *
 * "See https://example.com/docs." means the page, not a path ending in a dot. A
 * closing bracket is kept only when the URL opened one of its own, which is what
 * makes Wikipedia-style `…/Foo_(bar)` survive while `(see https://x.com)` loses
 * the bracket it borrowed from the sentence.
 */
function trimURL(url: string): string {
	let trimmed = url;
	let changed = true;
	/* Repeat until stable — "https://x.com)." needs the dot, then the bracket, removed. */
	while (changed) {
		changed = false;
		const last = trimmed.slice(-1);
		/* Sentence punctuation never ends a URL anyone meant to share. */
		if ('.,;:!?'.includes(last)) {
			trimmed = trimmed.slice(0, -1);
			changed = true;
		} else if (last === ')' || last === ']') {
			const open = last === ')' ? '(' : '[';
			/* An unbalanced closer belongs to the surrounding sentence. */
			if (trimmed.split(open).length < trimmed.split(last).length) {
				trimmed = trimmed.slice(0, -1);
				changed = true;
			}
		}
	}
	return trimmed;
}

/**
 * Splits a plain-text body into text and link runs.
 *
 * This is how message bodies are autolinked **without** `{@html}`: the component
 * renders text runs as text and link runs as anchors whose `href` is the run
 * itself — and since only `http:`/`https:` URLs match, a `javascript:` link cannot
 * be produced however the body is written.
 */
export function linkifyText(text: string): TextSegment[] {
	const segments: TextSegment[] = [];
	let cursor = 0;
	/* Each match becomes a link run, with the text since the previous one before it. */
	for (const match of text.matchAll(URL_PATTERN)) {
		const url = trimURL(match[0]);
		const start = match.index as number;
		/* Whatever precedes the link is ordinary text. */
		if (start > cursor) segments.push({ kind: 'text', text: text.slice(cursor, start) });
		segments.push({ kind: 'link', text: url });
		cursor = start + url.length;
	}
	/* The tail after the last link — or the whole body when there was none. */
	if (cursor < text.length) segments.push({ kind: 'text', text: text.slice(cursor) });
	return segments;
}

/**
 * Formats a span of minutes compactly: `45m`, `3h 5m`, `2d 4h`.
 *
 * Two units at most, since a countdown that says `2d 4h 13m` is precise about
 * the part nobody reads. The sign is dropped — the caller says "overdue by".
 */
export function formatSupportDuration(minutes: number): string {
	const total = Math.round(Math.abs(minutes));
	const days = Math.floor(total / 1440);
	const hours = Math.floor((total % 1440) / 60);
	const mins = total % 60;
	/* Days and hours once it runs past a day. */
	if (days > 0) return hours > 0 ? `${days}d ${hours}h` : `${days}d`;
	/* Hours and minutes once it runs past an hour. */
	if (hours > 0) return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
	return `${mins}m`;
}

/** How close an SLA target is to being missed. */
export type SupportSLAPhase = 'none' | 'met' | 'paused' | 'breached' | 'due-soon' | 'on-track';

/** One SLA clock's inputs, as a ticket carries them. */
export interface SupportSLAInput {
	/** When the target falls due, or nothing when the priority has none. */
	dueAt?: string | null;
	/** Whether the server has recorded a breach. */
	isBreached?: boolean;
	/** Whether the clock is paused (waiting on the customer). */
	isPaused?: boolean;
	/** When the target was met — the first response, or the resolution. */
	completedAt?: string | null;
}

/** Minutes before a due time at which a target counts as due soon. */
export const SUPPORT_SLA_DUE_SOON_MINUTES = 60;

/**
 * Where one SLA clock stands.
 *
 * The server's breach flag is trusted over the local clock — it is what fired
 * the breach email — but a due time already in the past reads as breached too,
 * so a ticket does not look on-track for up to five minutes until the next cron
 * pass sets the flag. `minutesRemaining` is negative once overdue and `null`
 * where a countdown means nothing (met, or no target).
 */
export function supportSLAState(
	input: SupportSLAInput,
	now: number = Date.now()
): { phase: SupportSLAPhase; minutesRemaining: number | null } {
	/* A target that was met stays met, late or not — only the flag says which. */
	if (input.completedAt) {
		return { phase: input.isBreached ? 'breached' : 'met', minutesRemaining: null };
	}
	/* No target for this priority. */
	if (!input.dueAt) return { phase: 'none', minutesRemaining: null };

	const minutesRemaining = Math.floor((new Date(input.dueAt).getTime() - now) / 60_000);
	/* Missed, by the server's record or by the clock. */
	if (input.isBreached || minutesRemaining < 0) return { phase: 'breached', minutesRemaining };
	/* Waiting on the customer: the due time moves out as long as it stays paused. */
	if (input.isPaused) return { phase: 'paused', minutesRemaining };
	return {
		phase: minutesRemaining <= SUPPORT_SLA_DUE_SOON_MINUTES ? 'due-soon' : 'on-track',
		minutesRemaining
	};
}

/** Formats a byte count for an attachment list: `812 B`, `14.2 KB`, `3.1 MB`. */
export function formatFileSize(bytes: number): string {
	/* Below a kilobyte, a decimal would be noise. */
	if (bytes < 1024) return `${bytes} B`;
	/* Kilobytes up to a megabyte. */
	if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
	return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Adds newly picked files to an attachment list, skipping any already there.
 *
 * A file counts as already there when one with the same name and size is —
 * which is what a second pick of the same file looks like, and what the list's
 * key is made of, so a duplicate would otherwise break the list's rendering.
 */
export function mergeAttachments<T extends { name: string; size: number }>(existing: T[], picked: T[]): T[] {
	const merged = [...existing];
	/* Each new file is checked against everything kept so far, picks included. */
	for (const file of picked) {
		if (!merged.some((kept) => kept.name === file.name && kept.size === file.size)) merged.push(file);
	}
	return merged;
}

/** A translation function, as `$t` / `t2` are. */
export type SupportTranslate = (key: string, vars?: Record<string, string | number>) => string;

/**
 * A name out of an event payload value.
 *
 * Assignee and category events may carry either the bare name or an object with
 * one, so both are read; anything else is treated as nobody.
 */
function eventName(value: unknown): string {
	/* The plain form. */
	if (typeof value === 'string') return value;
	/* The object form. */
	if (value && typeof value === 'object' && typeof (value as { name?: unknown }).name === 'string') {
		return (value as { name: string }).name;
	}
	return '';
}

/**
 * The sentence a system event renders as in the thread.
 *
 * The thread prints the event's author in front of it, so the participant
 * sentences are phrased as what that person did — and a removal whose author is
 * the person removed reads as them leaving. Events carry only the name, so that
 * is what is compared.
 *
 * Status and priority values are translated as labels, so "awaiting_customer"
 * never reaches a reader; an event type this client does not know still renders,
 * as a neutral "Ticket updated", rather than as nothing.
 */
export function describeSupportEvent(
	message: Pick<SupportMessage, 'eventType' | 'eventData'> & { author?: SupportPerson | null },
	translate: SupportTranslate,
	audience: SupportAudience = 'agent'
): string {
	const data = message.eventData ?? {};
	const from = data.from;
	const to = data.to;
	/* One sentence per event type. */
	switch (message.eventType) {
		case 'created':
			return translate('Ticket opened');
		case 'status':
			return translate('Status changed to {{to}}', {
				to: translate(supportStatusLabel(to as SupportTicketStatus, audience))
			});
		case 'priority':
			return translate('Priority changed from {{from}} to {{to}}', {
				from: translate(supportPriorityLabel(from as SupportTicketPriority)),
				to: translate(supportPriorityLabel(to as SupportTicketPriority))
			});
		case 'assignee': {
			const name = eventName(to);
			/* Nobody: an unassignment. */
			if (!name) return translate('Unassigned');
			/* Routing picked the assignee — said so, since no person did it. */
			return data.auto === true
				? translate('Assigned to {{name}} automatically', { name })
				: translate('Assigned to {{name}}', { name });
		}
		case 'category': {
			const name = eventName(to);
			return name ? translate('Category set to {{name}}', { name }) : translate('Category cleared');
		}
		case 'auto_resolved':
			return translate('Resolved automatically after no reply');
		case 'auto_closed':
			return translate('Closed automatically');
		case 'participant_added':
			return translate('Added {{name}} to the ticket', { name: eventName(data.name) });
		case 'participant_removed': {
			const name = eventName(data.name);
			/* Removing oneself is leaving: matched by identity where the event carries it
			   (names are not unique), by name for events recorded before it did. */
			const removedUUID = typeof data.userUUID === 'string' ? data.userUUID : null;
			const isSelf = removedUUID
				? message.author?.userUUID === removedUUID
				: !!message.author && message.author.name === name;
			return isSelf
				? translate('Left the ticket')
				: translate('Removed {{name}} from the ticket', { name });
		}
		case 'attachment_removed':
			return translate('Deleted the attachment {{filename}}', { filename: String(data.filename ?? '') });
		case 'rated':
			return translate('Rated {{rating}} out of 5', { rating: Number(data.rating ?? to ?? 0) });
		default:
			return translate('Ticket updated');
	}
}

/** Whether a ticket-key prefix is well formed: two to ten uppercase letters or digits. */
export function isValidTicketPrefix(prefix: string): boolean {
	return /^[A-Z0-9]{2,10}$/.test(prefix);
}

/**
 * A best guess at the reader's operating system, to prefill the ticket form.
 *
 * Only a default — the field stays editable, and the product may well run on a
 * different machine from the browser filing the ticket. iOS is tested before
 * macOS because iPad Safari reports itself as a Macintosh.
 */
export function guessOperatingSystem(userAgent: string): string {
	/* Windows first: it is the one agent string nothing else imitates. */
	if (/Windows/i.test(userAgent)) return 'Windows';
	/* Mobile before desktop, as above. */
	if (/iPhone|iPad|iPod/i.test(userAgent)) return 'iOS';
	/* Desktop Apple. */
	if (/Mac OS X|Macintosh/i.test(userAgent)) return 'macOS';
	/* Android before Linux, which it also claims to be. */
	if (/Android/i.test(userAgent)) return 'Android';
	/* Every other Linux. */
	if (/Linux/i.test(userAgent)) return 'Linux';
	return '';
}

/**
 * Makes an avatar URL loadable from the client.
 *
 * Absolute and `data:` URLs pass through; a server-relative one is resolved
 * against the API origin, which in development is not the page's own.
 */
export function resolveSupportAvatarURL(url: string | null | undefined, apiBase: string): string | null {
	/* No avatar at all. */
	if (!url) return null;
	/* Already loadable. */
	if (/^(https?:|data:)/i.test(url)) return url;
	return `${apiBase.replace(/\/+$/, '')}/${url.replace(/^\/+/, '')}`;
}

/**
 * Renders an SLA target (minutes) as the hours an administrator edits.
 *
 * Hours rather than minutes because targets are set in hours and days; `null`
 * (no target) is the empty field.
 */
export function slaMinutesToHours(minutes: number | null): string {
	/* No target. */
	if (minutes === null) return '';
	return String(Math.round((minutes / 60) * 100) / 100);
}

/**
 * Parses the hours an administrator typed back into minutes.
 *
 * Blank is `null` — no target. Anything that is not a positive number is
 * `undefined`, so the form can refuse it rather than save a nonsensical target.
 */
export function slaHoursToMinutes(text: string): number | null | undefined {
	const trimmed = text.trim();
	/* Blank means no target. */
	if (trimmed === '') return null;
	const hours = Number(trimmed);
	/* Only a positive number is a target. */
	if (!Number.isFinite(hours) || hours <= 0) return undefined;
	return Math.round(hours * 60);
}

/**
 * Image types the support desk shows as a thumbnail and previews inline — the
 * raster formats the server's thumbnailer reads. SVG is deliberately absent: it
 * is a document that can carry script, and it gets the plain file chip.
 */
export const PREVIEWABLE_IMAGE_TYPES = new Set([
	'image/png',
	'image/jpeg',
	'image/gif',
	'image/webp',
	'image/avif',
	'image/tiff'
]);

/** Whether an attachment of this MIME type is shown as an image. Case and parameters are ignored. */
export function isPreviewableImage(contentType: string | null | undefined): boolean {
	const type = (contentType ?? '').split(';')[0].trim().toLowerCase();
	return PREVIEWABLE_IMAGE_TYPES.has(type);
}

/** One attachment of a ticket's thread, with the message that carried it. */
export interface SupportThreadAttachment {
	/** The attachment. */
	attachment: SupportAttachment;
	/** The message it was uploaded with — its author and whether it was an internal note. */
	message: SupportMessage;
}

/**
 * Every attachment in a ticket's thread, newest first.
 *
 * Built from the messages the ticket already carries, so the attachments panel
 * needs no request of its own — and so it shows exactly what the reader's thread
 * shows: a customer's thread holds no internal notes, so neither does their list.
 * Ordered by upload time; equal times fall back to reverse thread order, so the
 * list is the thread read backwards.
 */
export function collectTicketAttachments(messages: SupportMessage[]): SupportThreadAttachment[] {
	const entries = messages.flatMap((message) => message.attachments.map((attachment) => ({ attachment, message })));
	/* Equal timestamps (one message's files) fall back to position: later in the thread, earlier in the list. */
	return entries
		.map((entry, index) => ({ entry, index }))
		.sort((a, b) => Date.parse(b.entry.attachment.created) - Date.parse(a.entry.attachment.created) || b.index - a.index)
		.map(({ entry }) => entry);
}

/**
 * An upload's progress as a whole percentage for a progress bar, clamped to
 * 0–100 — a browser can report a final event slightly past the total.
 */
export function uploadPercent(fraction: number): number {
	/* NaN (a zero-length body) reads as nothing sent yet. */
	if (!Number.isFinite(fraction)) return 0;
	return Math.min(100, Math.max(0, Math.round(fraction * 100)));
}
